import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { message, Spin, Button, Card, Table, Form, Row, Col, Typography, Space, Tag, Flex } from 'antd';
import { PrinterOutlined, CreditCardOutlined, FileTextOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  TextField,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  IconButton,
  Avatar,
  InputAdornment,
  Chip,
  OutlinedInput,
  Typography as MuiTypography,
  FormHelperText,
  Container
} from '@mui/material';
import {
  AttachMoney as CashIcon,
  AccountBalance as BankIcon,
  Receipt as CheckIcon,
  CreditCard as CardIcon,
  Payment as PaymentActionIcon,
  Close as CloseIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import Swal from 'sweetalert2';
import HttpsIcon from '@mui/icons-material/Https';

// 
import { generateDebitNotePDF } from '../utils/generateDebitNotePDF';
import generateThermalPaymentReceipt from '../utils/generateThermalPaymentReceipt';
import { DebitNoteNotFound, DebitNoteError } from './DebitNoteStatus';
import LoadingInformation from '../../Components/LoadingInformation';

const { Title, Text } = Typography;

// Función helper para formatear moneda
function formatCurrency(value) {
  if (value === null || value === undefined) return 'RD$0.00';
  let numValue;
  if (typeof value === 'string') {
    numValue = parseFloat(value.replace(/,/g, ''));
  } else if (typeof value === 'number') {
    numValue = value;
  } else {
    return 'RD$0.00';
  }
  return numValue.toLocaleString('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

// Función helper para formatear fecha
function formatDate(dateString) {
  if (!dateString) return 'No registrada';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-DO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function parseNumber(str) {
  if (!str) return 0;
  return Number(str.toString().replace(/,/g, ''));
}

function toMySQLDatetime(dateString) {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

function DebitNoteView() {
  const { debitNoteId } = useParams();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();

  // Estados principales
  const [loading, setLoading] = useState(true);
  const [debitNote, setDebitNote] = useState(null);
  const [debitNoteDetails, setDebitNoteDetails] = useState([]);
  const [invoiceData, setInvoiceData] = useState(null);
  const [paymentReceipts, setPaymentReceipts] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);
  const [permissionError, setPermissionError] = useState(false);

  // Estados para diálogo de pago
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentForm] = Form.useForm();
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedServicesDetailed, setSelectedServicesDetailed] = useState([]);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [reference, setReference] = useState('');
  const [bank, setBank] = useState('');
  const [banksOptions, setBanksOptions] = useState([]);
  const [paymentDescription, setPaymentDescription] = useState('');
  const [printerConfig, setPrinterConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  // Estados para extensión de Chrome
  const [extensionInstalled, setExtensionInstalled] = useState(false);
  const [extensionChecked, setExtensionChecked] = useState(false);
  // Edición por servicio
  const [showPerServiceInputs, setShowPerServiceInputs] = useState(false);
  const [serviceAmounts, setServiceAmounts] = useState({}); // { detailID: numericAmount }
  const [serviceInputValues, setServiceInputValues] = useState({}); // valores crudos de texto
  const [uncommittedServices, setUncommittedServices] = useState([]); // IDs que aún no cumplen regla
  // Expansión de detalles de recibos
  const [expandedReceipts, setExpandedReceipts] = useState([]); // array de transactionID

  const checkPermissions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setPermissionError(true);
      return messageApi.error({
        content: 'No se encontró el token de autenticación. Por favor, vuelve a iniciar sesión.',
        duration: 5
      }).then(() => {
        navigate('/sign-in');
      });
    }
    setPermissionError(false);
    setLoading(true);
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/users/check-modify-role-permissions', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.status === 403) {
        messageApi.error('No tienes permisos para acceder a esta sección.');
        setPermissionError(true);
        throw new Error('No tienes permisos para acceder a esta sección.');
      }
    } catch (error) {
      messageApi.error(error.message || 'Error al verificar permisos.');
      throw error; // Re-throw to handle it in the calling function
    }
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Verificar extensión al montar el componente
  useEffect(() => {
    const checkExtension = async () => {
      try {
        const isInstalled = await checkExtensionRealTime();
        setExtensionChecked(true);
        if (!isInstalled) {
          messageApi.warning('La extensión "Proxy Impresora Térmica Local" no está instalada. Instálala para imprimir recibos térmicos.');
        }
      } catch (error) {
        console.error('Error verificando extensión:', error);
        setExtensionChecked(true);
        messageApi.warning('No se pudo verificar la extensión de impresión térmica.');
      }
    };

    if (!isMobile) {
      checkExtension();
    } else {
      setExtensionChecked(true);
    }
  }, [isMobile]);

  const fetchDebitNoteData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        messageApi.error('Token de autenticación no encontrado');
        navigate('/login');
        return;
      }
      if (!debitNoteId) {
        messageApi.error('ID de nota de débito no proporcionado');
        return;
      }

      const response = await fetch(`https://dprincess-training.servehttp.com/debit-notes/list-note/${debitNoteId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 404) {
        setNotFound(true);
        return;
      }

      if (!response.ok) throw new Error('Error al obtener datos de la nota de débito');

      const data = await response.json();

      setDebitNote(data.debitNote);
      setDebitNoteDetails(data.debitNoteDetails);
      setInvoiceData(data.invoiceData);
      setPaymentReceipts(data.paymentReceipts || []);

    } catch (error) {
      console.error('Error fetching debit note data:', error);
      messageApi.error('Error al cargar los datos de la nota de débito');
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://dprincess-training.servehttp.com/billing-services/bankAccounts/list`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBanksOptions(data);
      } else {
        messageApi.open({
          type: 'error',
          content: 'Error al cargar las cuentas bancarias, por favor intente nuevamente.',
        });
      }
    } catch (error) {
      console.error('Error:', error);
      messageApi.open({
        type: 'error',
        content: 'Error de conexión al cargar las cuentas bancarias.',
      });
    }
  };

  useEffect(() => {
    if (debitNoteId) {
      fetchDebitNoteData();
    }
  }, [debitNoteId]);

  useEffect(() => {
    fetchBankAccounts();
  }, []);

  const expandedRowRender = (record) => {
    const originalSubtotal = parseNumber(record.servicePrice) * record.serviceQty * (1 - (record.discount || 0) / 100);
    const newSubtotal = parseNumber(record.newServicePrice) * record.newServiceQty * (1 - (record.newServiceDiscount || 0) / 100);

    return (
      <Box sx={{ p: 2, backgroundColor: '#fafafa', border: '1px solid #e8e8e8', borderRadius: '8px' }}>
        <Row gutter={16}>
          <Col span={12}>
            <Typography.Title level={5}>Servicio Original</Typography.Title>
            <Text strong>Servicio:</Text> <Text>{record.serviceName}</Text><br />
            <Text strong>Precio:</Text> <Text>{formatCurrency(record.servicePrice)}</Text><br />
            <Text strong>Cantidad:</Text> <Text>{record.serviceQty}</Text><br />
            <Text strong>Descuento:</Text> <Text>{record.discount || 0}%</Text><br />
            <Text strong>Subtotal:</Text> <Text>{formatCurrency(originalSubtotal)}</Text>
          </Col>
          <Col span={12}>
            <Typography.Title level={5}>Servicio Nuevo/Modificado</Typography.Title>
            <Text strong>Servicio:</Text> <Text>{record.newServiceName}</Text><br />
            <Text strong>Precio:</Text> <Text>{formatCurrency(record.newServicePrice)}</Text><br />
            <Text strong>Cantidad:</Text> <Text>{record.newServiceQty}</Text><br />
            <Text strong>Descuento:</Text> <Text>{record.newServiceDiscount || 0}%</Text><br />
            <Text strong>Subtotal:</Text> <Text>{formatCurrency(newSubtotal)}</Text>
          </Col>
        </Row>
      </Box>
    );
  };

  const handlePrintDebitNote = async () => {
    if (!debitNote || !invoiceData || !debitNoteDetails) {
      messageApi.error('No hay datos suficientes para generar el PDF');
      return;
    }
    try {
      messageApi.info('Generando PDF de la nota de débito...');
      const pdf = generateDebitNotePDF(debitNote, invoiceData, debitNoteDetails);
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const newWindow = window.open(pdfUrl, '_blank');
      if (!newWindow) {
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `Nota_Débito_${debitNote.debitNoteNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(pdfUrl);
        messageApi.info('Se ha descargado el PDF de la nota de débito');
      }
    } catch (error) {
      console.error('Error generando PDF:', error);
      messageApi.error('Error al generar el PDF de la nota de débito', error);
    }
  };

  const openPaymentModal = () => {
    const pendingDetails = debitNoteDetails.filter(detail => {
      if (parseNumber(detail.amount) <= 0) return false;
      const remaining = getRemainingForDetail(detail);
      return remaining > 0; // aún queda por pagar
    });
    if (pendingDetails.length === 0) {
      messageApi.info('No hay cargos pendientes de pago en esta nota de débito.');
      return;
    }
    setSelectedServices([]);
    setSelectedServicesDetailed([]);
    setPaymentAmount(0);
    setPaymentMethod('efectivo');
    paymentForm.resetFields();
    setPaymentDialogOpen(true);
  };

  const handleServiceSelectionForPayment = (selectedServiceIds) => {
    setSelectedServices(selectedServiceIds);
    // Si se quitaron servicios, limpiar montos previos
    setServiceAmounts(prev => {
      const updated = {};
      selectedServiceIds.forEach(id => {
        const detail = debitNoteDetails.find(d => d.debitNoteDetailID === id);
        if (detail) {
          const numeric = getRemainingForDetail(detail); // usar restante
          updated[id] = prev[id] !== undefined ? prev[id] : numeric; // conservar si existía
        }
      });
      return updated;
    });
    setServiceInputValues(prev => {
      const updated = {};
      selectedServiceIds.forEach(id => {
        const detail = debitNoteDetails.find(d => d.debitNoteDetailID === id);
        if (detail) {
          const numeric = getRemainingForDetail(detail);
          updated[id] = prev[id] !== undefined ? prev[id] : numeric.toFixed(2);
        }
      });
      return updated;
    });
    let totalAmount = 0;
    const amounts = [];
    selectedServiceIds.forEach(serviceId => {
      const detail = debitNoteDetails.find(d => d.debitNoteDetailID === serviceId);
      if (detail) {
        const amount = getRemainingForDetail(detail);
        amounts.push(amount);
        totalAmount += amount;
      }
    });
    setPaymentAmount(totalAmount);
    const selectedServicesDetailed = selectedServiceIds.map((serviceId, idx) => {
      const detail = debitNoteDetails?.find(d => d.debitNoteDetailID === serviceId);
      const selectedServices = invoiceData.invoiceAllDetails.find(d => d.selectedServiceID === Number(detail.selectedServiceID));
      return {
        selectedServiceID: selectedServices.selectedServiceID,
        debitNoteDetailID: detail.debitNoteDetailID,
        amount: serviceAmounts[serviceId] !== undefined ? serviceAmounts[serviceId] : (amounts[idx] || 0), // monto restante a pagar
        concept: `Pago para: ${detail.concept}`
      };
    });
    setSelectedServicesDetailed(selectedServicesDetailed);
  };

  const togglePerServiceInputs = () => {
    if (selectedServices.length === 0) return;
    setShowPerServiceInputs(v => !v);
  };

  const handleChangeServiceAmount = (detailID, raw) => {
    // Permitir vacío temporal
    if (raw === '') {
      setServiceInputValues(prev => ({ ...prev, [detailID]: '' }));
      setServiceAmounts(prev => {
        const copy = { ...prev };
        delete copy[detailID];
        return copy;
      });
      return;
    }
    // Aceptar sólo dígitos y punto
    const sanitized = raw.replace(/[^0-9.]/g, '');
    // Evitar múltiples puntos
    const parts = sanitized.split('.');
    let normalized = parts[0];
    if (parts.length > 1) normalized += '.' + parts.slice(1).join('').slice(0, 2); // dos decimales
    setServiceInputValues(prev => ({ ...prev, [detailID]: normalized }));

    const num = parseFloat(normalized);
    if (isNaN(num)) {
      setServiceAmounts(prev => { const copy = { ...prev }; delete copy[detailID]; return copy; });
      return;
    }
    const detail = debitNoteDetails.find(d => d.debitNoteDetailID === detailID);
    const full = detail ? getRemainingForDetail(detail) : 0; // máximo a pagar ahora
    // Regla: sólo comprometer si >= 100 o igual al monto restante
    if (num >= 100 || Math.abs(num - full) < 0.001) {
      const capped = num > full ? full : num; // no exceder el restante
      setServiceAmounts(prev => ({ ...prev, [detailID]: capped }));
      // Actualizar visual si excedió
      if (num > full) {
        setServiceInputValues(prev => ({ ...prev, [detailID]: full.toFixed(2) }));
      }
    } else {
      // Quitar de comprometidos
      setServiceAmounts(prev => { const copy = { ...prev }; delete copy[detailID]; return copy; });
    }
  };

  // Recalcular totales cuando cambian los montos individuales
  useEffect(() => {
    if (selectedServices.length === 0) {
      setPaymentAmount(0);
      setUncommittedServices([]);
      return;
    }
    const committedIds = selectedServices.filter(id => serviceAmounts[id] !== undefined);
    const total = committedIds.reduce((acc, id) => acc + serviceAmounts[id], 0);
    setPaymentAmount(total);
    setSelectedServicesDetailed(committedIds.map(id => {
      const detail = debitNoteDetails.find(d => d.debitNoteDetailID === id);
      const selectedServices = invoiceData.invoiceAllDetails.find(d => d.selectedServiceID === Number(detail.selectedServiceID));
      return {
        selectedServiceID: selectedServices.selectedServiceID,
        debitNoteDetailID: detail.debitNoteDetailID,
        amount: serviceAmounts[id],
        concept: `Pago para: ${detail?.concept}`
      };
    }));
    const uncommitted = selectedServices.filter(id => serviceAmounts[id] === undefined);
    setUncommittedServices(uncommitted);
  }, [serviceAmounts, selectedServices]);

  // Calcular lo ya pagado por detalle según recibos.
  const getAlreadyPaidForDetail = (detail) => {
    if (!detail) return 0;
    const serviceKey = detail.newServiceID || detail.selectedServiceID; // priorizar el servicio nuevo
    if (!serviceKey) return 0;
    return paymentReceipts.reduce((acc, r) => {
      const dets = r.details || [];
      const sum = dets.reduce((s, d) => s + (d.selectedService === serviceKey ? parseNumber(d.amountPaid) : 0), 0);
      return acc + sum;
    }, 0);
  };

  const getRemainingForDetail = (detail) => {
    const total = parseNumber(detail.amount);
    const paid = getAlreadyPaidForDetail(detail);
    const remaining = total - paid;
    return remaining > 0 ? remaining : 0;
  };

  // Función helper para enviar mensajes a la extensión
  const sendToExtension = async (message) => {
    return new Promise((resolve, reject) => {
      const extensionId = localStorage.getItem('thermalPrinterExtensionId') || 'your-extension-id-here';

      if (!window.chrome || !window.chrome.runtime) {
        console.error('Chrome runtime no disponible');
        reject(new Error('Chrome runtime no disponible'));
        return;
      }

      // Intentar primero con el ID dinámico de localStorage
      window.chrome.runtime.sendMessage(extensionId, message, (response) => {
        if (window.chrome.runtime.lastError) {
          console.log('Error con ID dinámico, intentando con proxy global:', window.chrome.runtime.lastError.message);

          // Si falla, intentar con el proxy global
          if (window.chrome.runtime.sendMessage) {
            window.chrome.runtime.sendMessage(message, (response) => {
              if (window.chrome.runtime.lastError) {
                console.error('Error con proxy global:', window.chrome.runtime.lastError.message);
                reject(new Error('No se pudo conectar con la extensión'));
                return;
              }
              resolve(response);
            });
          } else {
            reject(new Error('No se pudo conectar con la extensión'));
          }
        } else {
          resolve(response);
        }
      });
    });
  };

  // Función para verificar la extensión en tiempo real
  const checkExtensionRealTime = async () => {
    try {
      const response = await sendToExtension({ action: 'ping' });
      if (response && response.success) {
        setExtensionInstalled(true);
        return true;
      } else {
        setExtensionInstalled(false);
        return false;
      }
    } catch (error) {
      console.error('Error verificando extensión:', error);
      setExtensionInstalled(false);
      return false;
    }
  };

  const fetchAgentPrinterConfiguration = async () => {
    setConfigLoading(true);
    try {
      // Verificar extensión antes de intentar obtener configuración
      const isInstalled = await checkExtensionRealTime();
      if (!isInstalled) {
        console.log('Extensión no instalada para fetchAgentPrinterConfiguration');
        messageApi.warning('La extensión "Proxy Impresora Térmica Local" no está instalada. Instálala para obtener configuración de impresora.');
        setPrinterConfig({ SelectedPrinterName: null, SuggestedCharsPerLine: 48, IsPrinterSelected: false });
        return;
      }

      console.log('Obteniendo configuración de impresora via extensión');
      const response = await sendToExtension({
        action: 'proxyRequest',
        path: '/printer-config',
        method: 'GET'
      });

      if (response && response.success) {
        const config = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
        setPrinterConfig(config);
        if (config.IsPrinterSelected) {
          messageApi.success(`Configuración de impresora '${config.SelectedPrinterName}' (${config.SuggestedCharsPerLine} car.) obtenida.`);
        } else {
          messageApi.info(`Agente no tiene impresora predeterminada. Usando ancho ${config.SuggestedCharsPerLine} car.`);
        }
      } else {
        const errorMsg = response && response.error ? response.error : 'Respuesta inválida de la extensión';
        console.error('Error en respuesta de la extensión:', errorMsg);
        messageApi.error(`Fallo al obtener configuración de impresora: ${errorMsg}`);
        setPrinterConfig({ SelectedPrinterName: null, SuggestedCharsPerLine: 48, IsPrinterSelected: false });
      }
    } catch (error) {
      console.error("Error en fetchAgentPrinterConfiguration via extensión:", error);
      messageApi.error("Error al contactar extensión para configuración de impresora.");
      setPrinterConfig({ SelectedPrinterName: null, SuggestedCharsPerLine: 48, IsPrinterSelected: false });
    } finally {
      setConfigLoading(false);
    }
  };

  const handleDirectThermalPrinter = async (escposData) => {
    // Chequear la extensión en tiempo real antes de imprimir
    const isInstalled = await checkExtensionRealTime();
    if (!isInstalled) {
      console.log('Extensión no instalada para handleDirectThermalPrinter');
      return { success: false, message: 'La extensión "Proxy Impresora Térmica Local" no está instalada. Instálala para imprimir.' };
    }

    try {
      // Asegurar que tenemos configuración de impresora antes de imprimir
      const printerName = printerConfig?.IsPrinterSelected ? printerConfig.SelectedPrinterName : null;

      console.log('Enviando proxyRequest para /print via extensión');
      const response = await sendToExtension({
        action: 'proxyRequest',
        path: '/print',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: {
          data: escposData,
          printerName: printerName,
          printType: 'RAW'
        }
      });

      if (response && response.success) {
        const printerUsed = printerName || 'impresora predeterminada';
        return { success: true, message: `Documento enviado a ${printerUsed}: ${response.data}` };
      } else {
        const errorMsg = response && response.error ? response.error : 'Respuesta inválida de la extensión';
        console.error('Error en respuesta de la extensión:', errorMsg);
        return { success: false, message: `Error de impresión: ${errorMsg}` };
      }
    } catch (error) {
      console.error('Error al enviar el recibo térmico via extensión:', error);
      return { success: false, message: 'Error de conexión con la impresora. Verifique que esté conectada y el agente en ejecución.' };
    }
  };

  const generateAndPrintPaymentReceipt = async (paymentResult) => {
    try {
      Swal.fire({ title: 'Imprimiendo recibo de pago...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      if (!printerConfig) await fetchAgentPrinterConfiguration();

      const paperWidthCharsProvided = printerConfig?.SuggestedCharsPerLine || 48;

      const paymentData = {
        paymentReceipt: { ...paymentResult.paymentReceipt, registeredByUserName: invoiceData?.employeeName || 'Usuario no identificado' },
        paymentReceiptDetails: paymentResult.paymentReceiptDetails,
        invoiceData,
        debitNote,
        debitNoteDetails,
      };

      const thermalReceiptData = await generateThermalPaymentReceipt(paymentData, paperWidthCharsProvided);
      if (!thermalReceiptData) {
        Swal.close();
        Swal.fire({
          icon: 'error', title: 'Error',
          text: 'Pago registrado con éxito, pero no se pudieron generar los datos del recibo térmico.'
        });
        return;
      }

      const printResult = await handleDirectThermalPrinter(thermalReceiptData);
      if (printResult.success) {
        Swal.close();
        Swal.fire({ icon: 'success', title: 'Impresión Exitosa', text: 'Pago registrado y recibo impreso.' });
        setPaymentDialogOpen(false);
      } else {
        Swal.close();
        Swal.fire({ icon: 'warning', title: 'Error de Impresión', text: 'Pago registrado, pero hubo un problema al imprimir.' });
        setPaymentDialogOpen(false);
      }
    } catch (error) {
      Swal.close();
      console.error('Error generando recibo de pago:', error);
      Swal.fire({ icon: 'error', title: 'Fallo al imprimir', text: 'No se pudo imprimir el recibo de pago.' });
    }
  };

  const reprintThermalPaymentReceipt = async (receipt) => {
    try {
      Swal.fire({
        title: 'Reimprimiendo recibo de pago...',
        allowOutsideClick: false,
        text: 'Por favor, espere mientras se procesa la solicitud.',
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading()
      });

      // Asegurar configuración de impresora
      if (!printerConfig) {
        await fetchAgentPrinterConfiguration();
      }
      const paperWidthCharsProvided = printerConfig?.SuggestedCharsPerLine || 48;

      // Normalizar estructura del recibo (puede venir de API con distintos nombres)
      const totalPaid = parseNumber(receipt.totalAmountReceived || receipt.totalAmountPaid || receipt.totalAmount || 0);
      const paymentReceipt = {
        paymentReceiptID: receipt.paymentReceiptID || receipt.transactionID,
        receiptDocumentNumber: receipt.receiptDocumentNumber || receipt.documentNumber,
        paymentDate: receipt.paymentDate || receipt.transactionDate || receipt.createdAt,
        totalAmountReceived: totalPaid,
        totalAmountPaid: totalPaid, // por compatibilidad con generadores que usan otra key
        paymentMethod: receipt.paymentMethod,
        referenceNumber: receipt.referenceNumber,
        bank: receipt.bank || null,
        notes: receipt.notes,
        registeredByUserName: invoiceData?.employeeName || 'Usuario no identificado'
      };

      // Mapear detalles
      const paymentReceiptDetails = (receipt.details || []).map(d => {
        const debitDetail = debitNoteDetails.find(dd => dd.debitNoteDetailID === (d.debitNoteDetailID || d.debitNoteDetailId));
        return {
          ...d,
          debitNoteDetailID: d.debitNoteDetailID || d.debitNoteDetailId,
          amountPaid: parseNumber(d.amountPaid || d.amount || 0),
          // Concepto: usar el del detalle o derivar del concepto de la nota de débito
          concept: d.concept || `Pago cargo ${debitDetail?.concept || ''}`.trim(),
          selectedServiceID: d.selectedServiceID || d.selectedService || debitDetail?.selectedServiceID,
          serviceQty: debitDetail?.serviceQty || 1
        };
      });

      const paymentData = {
        paymentReceipt,
        paymentReceiptDetails,
        invoiceData,
        debitNote,
        debitNoteDetails
      };

      const thermalReceiptData = await generateThermalPaymentReceipt(paymentData, paperWidthCharsProvided);
      if (!thermalReceiptData) {
        Swal.close();
        await Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudieron generar los datos del recibo para reimprimir.' });
        messageApi.error('Error al regenerar datos térmicos del recibo');
        return;
      }

      const printResult = await handleDirectThermalPrinter(thermalReceiptData);
      Swal.close();
      if (!printResult?.success) {
        await Swal.fire({ icon: 'error', title: 'Error de impresión', text: 'Fallo al reimprimir el recibo. Verifique la impresora.' });
        messageApi.error('Error al reimprimir recibo térmico');
      } else {
        await Swal.fire({ icon: 'success', title: 'Reimpresión exitosa', text: `Recibo ${paymentReceipt.receiptDocumentNumber || ''} reimpreso.` });
        messageApi.success('Recibo térmico reimpreso');
      }
    } catch (error) {
      Swal.close();
      console.error('Error reimprimiendo recibo de pago:', error);
      messageApi.error('Error inesperado reimprimiendo recibo');
      await Swal.fire({ icon: 'error', title: 'Fallo al reimprimir', text: 'No se pudo reimprimir el recibo térmico.' });
    }
  };

  const handlePaymentSubmit = async () => {
    if (selectedServices.length === 0 || paymentAmount <= 0) {
      messageApi.error('Debe seleccionar servicios y el monto debe ser mayor a cero.');
      return;
    }

    const isConfirmed = await Swal.fire({
      title: '¿Confirmar pago?',
      text: `Se procesará un pago por ${formatCurrency(paymentAmount)}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, procesar',
      cancelButtonText: 'Cancelar'
    });
    if (!isConfirmed.isConfirmed) return;

    setLoading(true);
    Swal.fire({ title: 'Procesando pago...', allowOutsideClick: false, allowEscapeKey: false, didOpen: () => Swal.showLoading() });

    try {
      const token = localStorage.getItem('token');
      const paymentTotalAmount = parseNumber(paymentAmount);

      const paymentReceipt = {
        debitNoteID: debitNote.debitNoteID,
        invoiceID: invoiceData.invoiceID,
        paymentDate: toMySQLDatetime(new Date()),
        totalAmountReceived: paymentTotalAmount,
        paymentMethod: paymentMethod,
        referenceNumber: reference || null,
        bank: bank,
        notes: paymentDescription || `Pago de nota de débito ${debitNote.debitNoteNumber}`,
        transactionDate: toMySQLDatetime(new Date())
      };

      const paymentReceiptDetails = selectedServicesDetailed.map(service => {
        return {
          debitNoteDetailID: service.debitNoteDetailID,
          amountPaid: service.amount,
          selectedServiceID: service.selectedServiceID,
          concept: service.concept,
          createdAt: toMySQLDatetime(new Date())
        }
      });

      const debitNoteDetailsToUpdate = selectedServicesDetailed.map(service => {
        const detail = debitNoteDetails.find(d => d.debitNoteDetailID === service.debitNoteDetailID);
        const totalAmountToPaid = parseNumber(detail?.amount || 0);
        const alreadyPaid = getAlreadyPaidForDetail(detail);
        const cumulative = alreadyPaid + parseNumber(service.amount);
        return {
          debitNoteDetailID: service.debitNoteDetailID,
          paymentStatus: cumulative + 0.001 >= totalAmountToPaid ? 'pagado' : 'pendiente'
        };
      });

      const cashFlows = selectedServicesDetailed.map(service => {
        return {
          debitNoteDetailID: service.debitNoteDetailID,
          amount: service.amount,
          paymentMethod: paymentMethod,
          creationDate: toMySQLDatetime(new Date()),
          description: service.concept,
          selectedServiceID: service.selectedServiceID,
          clientID: invoiceData.clientID,
          flowType: 'ingreso'
        }
      });

      const postData = {
        debitNoteID: debitNote.debitNoteID,
        invoiceID: invoiceData.invoiceID,
        debitNoteDetails: debitNoteDetailsToUpdate,
        paymentReceipt,
        paymentReceiptDetails,
        cashFlows
      };

      const response = await fetch(`https://dprincess-training.servehttp.com/debit-notes/payment/${debitNote.debitNoteID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(postData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al procesar el pago');
      }

      const result = await response.json();
      Swal.close();
      messageApi.success('Pago procesado exitosamente');
      await Swal.fire({ title: 'Pago exitoso', text: `Se ha registrado el pago por ${formatCurrency(paymentTotalAmount)}`, icon: 'success' });

      await generateAndPrintPaymentReceipt(result);

      setPaymentDialogOpen(false);
      await fetchDebitNoteData();

    } catch (error) {
      Swal.close();
      console.error('Error processing payment:', error);
      messageApi.error(error.message || 'Error al procesar el pago');
      Swal.fire({ title: 'Error', text: error.message, icon: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isMobile && extensionChecked && extensionInstalled) {
      fetchAgentPrinterConfiguration();
    }
  }, [extensionChecked, extensionInstalled]);

  const detailsColumns = [
    { title: '#', dataIndex: 'index', key: 'index', width: 50, render: (_, __, index) => index + 1 },
    { title: 'Concepto', dataIndex: 'concept', key: 'concept' },
    { title: 'Monto Debitado', dataIndex: 'amount', key: 'amount', width: 140, align: 'right', render: (amount) => formatCurrency(amount) },
    {
      title: 'Estado', dataIndex: 'paymentStatus', key: 'paymentStatus', width: 140, align: 'center', render: (status) => (
        <Tag color={status === 'pendiente' ? 'orange' : 'green'} icon={status === 'pendiente' ? <ClockCircleOutlined /> : <CheckCircleOutlined />}>
          {status === 'pendiente' ? 'Pendiente' : 'Pagado'}
        </Tag>
      )
    }
  ];

  const paymentReceiptsColumns = [
    { title: 'Recibo', dataIndex: 'receiptDocumentNumber', key: 'receiptDocumentNumber', width: 160, render: (num) => num || 'N/A' },
    { title: 'Fecha', dataIndex: 'transactionDate', key: 'transactionDate', width: 180, render: (date) => formatDate(date) },
    { title: 'Método', dataIndex: 'paymentMethod', key: 'paymentMethod', width: 110, render: (method) => method?.charAt(0).toUpperCase() + method?.slice(1) || 'N/E' },
    { title: 'Monto Pagado', dataIndex: 'totalAmountReceived', key: 'totalAmountReceived', width: 150, align: 'right', render: (amount) => formatCurrency(amount) },
    { title: 'Referencia', dataIndex: 'referenceNumber', key: 'referenceNumber', width: 120, render: (ref) => ref || 'N/A' },
    { title: 'Notas', dataIndex: 'notes', key: 'notes', ellipsis: true },
    {
      title: 'Acciones', key: 'actions', width: 140, align: 'center',
      render: (_, record) => {
        const expanded = expandedReceipts.includes(record.transactionID);
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            <IconButton onClick={() => reprintThermalPaymentReceipt(record)} size="small" title="Imprimir recibo térmico">
              <PrintIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      }
    }
  ];

  const toggleReceiptExpand = (id) => {
    setExpandedReceipts(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
  };

  const renderReceiptDetails = (receipt) => {
    const details = receipt.details || [];
    if (!details.length) return <Text type="secondary">Sin detalles registrados.</Text>;
    return (
      <Box sx={{ mt: 1 }}>
        <Table
          size="small"
          pagination={false}
          rowKey={(d) => d.paymentreceiptdetailID || `${d.serviceName}-${d.createdAt}`}
          columns={[
            { title: '#', dataIndex: 'paymentreceiptdetailID', key: 'idx', width: 60, render: (_, __, idx) => idx + 1 },
            { title: 'Servicio', dataIndex: 'serviceName', key: 'serviceName' },
            { title: 'Concepto', dataIndex: 'concept', key: 'concept', ellipsis: true },
            { title: 'Cant', dataIndex: 'serviceQty', key: 'serviceQty', width: 70, align: 'center' },
            { title: 'Monto', dataIndex: 'amountPaid', key: 'amountPaid', width: 120, align: 'right', render: (v) => formatCurrency(v) }
          ]}
          dataSource={details}
        />
        <Box sx={{ textAlign: 'right', mt: 1, fontWeight: 600 }}>
          Total Detalles: {formatCurrency(details.reduce((acc, d) => acc + parseNumber(d.amountPaid), 0))}
        </Box>
      </Box>
    );
  };

  if (loading) return <LoadingInformation />;
  if (permissionError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        {contextHolder}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
            {/* Icono de candado para indicar acceso restringido */}
            <HttpsIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 700 }}>
            No tienes permiso para acceder a esta sección
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
            Esta información solo está disponible para usuarios con permisos de administrador.
          </Typography>
        </Box>
      </Container>
    );
  };
  if (notFound) return <DebitNoteNotFound />;
  if (error) return <DebitNoteError onRetry={fetchDebitNoteData} />;

  return (
    <div style={{ padding: isMobile ? '1rem' : '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      {contextHolder}
      <Box sx={{ padding: '24px', backgroundColor: '#f0f2f5' }}>
        <Card style={{ marginBottom: '24px' }}>
          <Flex justify="space-between" align="center" wrap="wrap" gap="middle">
            <Title level={2} style={{ margin: 0, color: '#d32f2f' }}>Nota de Débito {debitNote.debitNoteNumber}</Title>
            <Flex gap="middle">
              <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrintDebitNote} size="large">Imprimir</Button>
              <Button icon={<CreditCardOutlined />} onClick={openPaymentModal} disabled={debitNote.paymentStatus === 'pagado'} size="large">Procesar Pago</Button>
            </Flex>
          </Flex>
        </Card>

        <Card title={<Space><FileTextOutlined />Información General</Space>} style={{ marginBottom: '24px' }}>
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={12}>
              <Text strong>Número:</Text> <Text>{debitNote.debitNoteNumber}</Text><br />
              <Text strong>Factura relacionada:</Text> <Text>{invoiceData?.invoiceNumber}</Text><br />
              <Text strong>Cliente:</Text> <Text>{invoiceData?.clientName}</Text><br />
              <Text strong>Fecha de emisión:</Text> <Text>{formatDate(debitNote.createdAt)}</Text>
            </Col>
            <Col xs={24} sm={12}>
              <Text strong>Monto total debitado:</Text> <Text style={{ color: '#66ef44ff', fontWeight: 'bold' }}>{formatCurrency(debitNote.amount)}</Text><br />
              <Text strong>Estado:</Text> <Tag color={debitNote.paymentStatus === 'pendiente' ? 'orange' : 'green'}>{debitNote.paymentStatus === 'pendiente' ? 'Pendiente de pago' : 'Pagado'}</Tag><br />
              <Text strong>Motivo:</Text> <Text>{debitNote.reason}</Text><br />
              <Text strong>Emitida por:</Text> <Text>{invoiceData?.employeeName}</Text>
            </Col>
          </Row>
        </Card>

        <Card title="Detalles de Cargos" style={{ marginBottom: '24px' }}>
          <Table
            columns={detailsColumns}
            dataSource={debitNoteDetails}
            rowKey="debitNoteDetailID"
            pagination={false}
            size="middle"
            expandable={{ expandedRowRender }}
          />
        </Card>

        {paymentReceipts.length > 0 && (
          <Card title="Recibos de Pago" style={{ marginBottom: '24px' }}>
            <Table
              columns={paymentReceiptsColumns}
              dataSource={paymentReceipts}
              rowKey={(r) => r.transactionID || r.paymentReceiptID}
              pagination={false}
              size="middle"
              expandable={{
                expandedRowKeys: expandedReceipts,
                onExpand: (expanded, record) => toggleReceiptExpand(record.transactionID),
                expandedRowRender: (record) => renderReceiptDetails(record),
                rowExpandable: (record) => (record.details && record.details.length > 0)
              }}
            />
          </Card>
        )}
      </Box>

      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} fullWidth maxWidth="sm" sx={{ overflowY: 'auto' }}>
        <DialogTitle sx={{ backgroundColor: 'rgba(59, 130, 246, 0.05)' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <MuiTypography variant="h6" sx={{ color: '#1d4ed8', fontWeight: 600 }}>Procesar Pago</MuiTypography>
            <IconButton onClick={() => setPaymentDialogOpen(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Servicios a pagar</InputLabel>
              <MuiSelect multiple value={selectedServices} onChange={(e) => handleServiceSelectionForPayment(e.target.value)} input={<OutlinedInput label="Servicios a pagar" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map(value => {
                      const detail = debitNoteDetails.find(d => d.debitNoteDetailID === value);
                      return <Chip key={value} label={`${detail?.concept} - ${formatCurrency(detail?.amount || 0)}`} size="small" />;
                    })}
                  </Box>
                )}>
                {debitNoteDetails.filter(d => d.paymentStatus === 'pendiente').map(d => (
                  <MenuItem key={d.debitNoteDetailID} value={d.debitNoteDetailID}>
                    <MuiTypography variant="body2">{d.concept} - Restante: {formatCurrency(getRemainingForDetail(d))} / Total: {formatCurrency(d.amount)}</MuiTypography>
                  </MenuItem>
                ))}
              </MuiSelect>
            </FormControl>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: showPerServiceInputs ? 1 : 2 }}>
              <TextField fullWidth label="Monto total a pagar" value={formatCurrency(paymentAmount)} disabled />
              {selectedServices.length > 0 && (
                <IconButton color={showPerServiceInputs ? 'primary' : 'default'} onClick={togglePerServiceInputs} title={showPerServiceInputs ? 'Ocultar montos por servicio' : 'Editar montos por servicio'}>
                  <DriveFileRenameOutlineIcon />
                </IconButton>
              )}
            </Box>
            {showPerServiceInputs && selectedServices.map(id => {
              const detail = debitNoteDetails.find(d => d.debitNoteDetailID === id);
              if (!detail) return null;
              const currentValue = serviceInputValues[id] !== undefined ? serviceInputValues[id] : '';
              const full = getRemainingForDetail(detail);
              const committed = serviceAmounts[id] !== undefined;
              return (
                <Box key={id} sx={{ mb: 1.5 }}>
                  <TextField
                    fullWidth
                    label={`Monto para: ${detail.concept} (Restante ${formatCurrency(full)})`}
                    value={currentValue}
                    placeholder={full.toFixed(2)}
                    onChange={(e) => handleChangeServiceAmount(id, e.target.value)}
                    helperText={
                      committed ? `Confirmado ${formatCurrency(serviceAmounts[id])}` : `Ingrese >= 100.00 o ${full.toFixed(2)} (restante) para confirmar`
                    }
                    InputProps={{
                      startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                    }}
                  />
                </Box>
              );
            })}
            {showPerServiceInputs && uncommittedServices.length > 0 && (
              <Box sx={{ mb: 2, mt: 1, fontSize: 12, color: 'warning.main' }}>
                {`Hay ${uncommittedServices.length} servicio(s) sin confirmar. El total sólo suma montos confirmados.`}
              </Box>
            )}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Método de pago</InputLabel>
              <MuiSelect value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} label="Método de pago">
                <MenuItem value="efectivo"><Box sx={{ display: 'flex', alignItems: 'center' }}><CashIcon sx={{ mr: 1 }} />Efectivo</Box></MenuItem>
                <MenuItem value="transferencia"><Box sx={{ display: 'flex', alignItems: 'center' }}><BankIcon sx={{ mr: 1 }} />Transferencia</Box></MenuItem>
                <MenuItem value="cheque"><Box sx={{ display: 'flex', alignItems: 'center' }}><CheckIcon sx={{ mr: 1 }} />Cheque</Box></MenuItem>
                <MenuItem value="tarjeta" disabled><Box sx={{ display: 'flex', alignItems: 'center' }}><CardIcon sx={{ mr: 1 }} />Tarjeta</Box></MenuItem>
              </MuiSelect>
            </FormControl>
            {
              (paymentMethod !== 'efectivo' && paymentMethod !== 'tarjeta') &&
              <FormControl fullWidth sx={{ mb: 2 }}>
                {banksOptions.length > 0 && (
                  <>
                    <InputLabel id="bank-label">Banco</InputLabel>
                    <MuiSelect
                      labelId="bank-label"
                      id="bank"
                      value={bank}
                      onChange={(e) => setBank(e.target.value)}
                      label="Banco"
                      fullWidth
                      MenuProps={{ disablePortal: true }}
                    >
                      {banksOptions.map((b) => (
                        <MenuItem key={b.id} value={b.id}>
                          {b.bank} - {b.accountNumber}
                        </MenuItem>
                      ))}
                    </MuiSelect>
                    <FormHelperText>Seleccione el banco para la transferencia</FormHelperText>
                  </>
                )}
                <TextField fullWidth label="Referencia" value={reference} onChange={(e) => setReference(e.target.value)} sx={{ mb: 2 }} />
              </FormControl>
            }
            <TextField fullWidth label="Descripción (opcional)" multiline rows={3} value={paymentDescription} onChange={(e) => setPaymentDescription(e.target.value)} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MuiButton onClick={() => setPaymentDialogOpen(false)} variant="outlined" color="error">Cancelar</MuiButton>
          <MuiButton onClick={handlePaymentSubmit} variant="contained" disabled={!paymentAmount || loading || uncommittedServices.length > 0 || selectedServices.length === 0} startIcon={<PaymentActionIcon />}>Procesar Pago</MuiButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default DebitNoteView;